import React, { useState, useEffect } from 'react';
import update from 'immutability-helper';
import { Modal, Tooltip, Button, Input, Select, Space } from 'antd';
import {
  ModalContent,
  CustomFieldRow,
  CustomFieldsContainer,
  CustomFieldRemove,
  CustomFieldError,
  ModalLabel,
  TooltipMark,
  ErrorAndButtonContainer,
} from './styled';
import useContainerRef from '@src/hooks/utils/useContainerRef';

const priorities = [
  {
    value: 3,
    label: 'P3 and above',
  },
  {
    value: 2,
    label: 'P2 and above',
  },
  {
    value: 1,
    label: 'P1 only',
  },
];

const ManualAlertCustomisation = ({ openModel, onSaveModel, onCancelModel, form }) => {
  const [selectedPriority, setSelectedPriority] = useState(priorities[0].value);
  const [customFields, setCustomFields] = useState<any>([]);
  const [emptyCustomFieldError, setEmptyCustomFieldError] = useState(false);

  const { containerRef, getContainer } = useContainerRef();

  useEffect(() => {
    if (openModel) {
      const maConfig = form.getFieldValue('maConfig');
      setSelectedPriority(Number(maConfig?.priority) || priorities[0].value);
      setCustomFields(maConfig?.customFields || []);
    }
  }, [openModel, form]);

  const addCustomFieldRow = () =>
    setCustomFields((prevCustomFields) => {
      const objKeys = Object.keys(prevCustomFields).sort((a: any, b: any) => a - b);
      const newKey = objKeys.length ? parseInt(objKeys[objKeys.length - 1], 10) + 1 : 0;
      return {
        ...prevCustomFields,
        [newKey]: {},
      };
    });

  const onKeyChange = (fieldKey, key) => {
    setCustomFields((prevCustomFields) =>
      update(prevCustomFields, {
        [fieldKey]: {
          key: {
            $set: key,
          },
        },
      }),
    );
  };

  const onValueChange = (fieldKey, value) => {
    setCustomFields((prevCustomFields) =>
      update(prevCustomFields, {
        [fieldKey]: {
          value: {
            $set: value,
          },
        },
      }),
    );
  };

  const onFieldRemove = (fieldKey) => {
    setCustomFields((prevCustomFields) => {
      const obj = { ...prevCustomFields };
      delete obj[fieldKey];
      return obj;
    });
  };

  const onUpdate = () => {
    const isCustomFieldEmpty = Object.keys(customFields).some((fieldKey) => {
      const field = customFields[fieldKey];
      return !field.key || !field.value;
    });

    if (isCustomFieldEmpty) {
      setEmptyCustomFieldError(true);
    } else {
      setEmptyCustomFieldError(false);

      onSaveModel('maConfig', {
        priority: selectedPriority,
        customFields: Object.values(customFields),
      });
    }
  };

  const footerButtons = [
    <Button key="cancel" onClick={onCancelModel}>
      Cancel
    </Button>,
    <Button key="update" type="primary" onClick={onUpdate}>
      Update
    </Button>,
  ];

  return (
    <Modal
      title="Manual Alerts Settings"
      width="740px"
      open={openModel === 'MA'}
      footer={footerButtons}
      onCancel={onCancelModel}
    >
      <ModalContent style={{ minHeight: 150 }} ref={containerRef}>
        <Space align="end" size={100}>
          <Space direction="vertical" size="small">
            <ModalLabel>
              Priority
              <Tooltip
                title="Specify the Manual alert priority level to assign to this alert."
                getPopupContainer={getContainer}
              >
                <TooltipMark />
              </Tooltip>
            </ModalLabel>

            <Select
              options={priorities}
              value={selectedPriority}
              onChange={setSelectedPriority}
              style={{ width: 300 }}
            />
          </Space>

          <ErrorAndButtonContainer>
            <Button key="add" onClick={addCustomFieldRow}>
              Add Custom Field
            </Button>
            {emptyCustomFieldError ? (
              <CustomFieldError>Empty values are not allowed</CustomFieldError>
            ) : null}
          </ErrorAndButtonContainer>
        </Space>

        <CustomFieldsContainer>
          {Object.keys(customFields).map((fieldKey) => {
            const field = customFields[fieldKey];

            return (
              <CustomFieldRow key={fieldKey}>
                <Input
                  value={field.key}
                  placeholder="key"
                  onChange={(e) => onKeyChange(fieldKey, e.target.value)}
                />
                <Input
                  value={field.value}
                  placeholder="value"
                  onChange={(e) => onValueChange(fieldKey, e.target.value)}
                />
                <CustomFieldRemove onClick={() => onFieldRemove(fieldKey)}>
                  &times;
                </CustomFieldRemove>
              </CustomFieldRow>
            );
          })}
        </CustomFieldsContainer>
      </ModalContent>
    </Modal>
  );
};

export default ManualAlertCustomisation;
